import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, FilePlus2, FileBadge, Mail, Printer } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DocTemplatesPanel } from "../components/DocTemplatesPanel";

function ModulisticaDialog({ contract, onClose }) {
  const { t, fmt, currentEntity, lang } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.post(`/contracts/${contract.id}/forms`).then((r) => setData(r.data)).catch(() => toast.error(t("msg.error")));
  }, [contract.id]);
  const today = new Date().toISOString().slice(0, 10);
  const entityName = currentEntity ? (lang === "it" ? currentEntity.name_it : currentEntity.name_en) : "";
  const sign = <div className="flex justify-between mt-8 text-sm"><div>{t("contracts.provider")}<div className="mt-10 border-t border-current w-48" /></div><div>{t("contracts.theClient")}<div className="mt-10 border-t border-current w-48" /></div></div>;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="modulistica-dialog">
        <DialogHeader>
          <DialogTitle>{t("contracts.forms")} — {contract.title}</DialogTitle>
        </DialogHeader>
        <div className="flex justify-end no-print">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-forms-button">
            <Printer className="h-4 w-4 mr-2" />{t("common.print")}
          </Button>
        </div>
        {!data ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div> : (
          <div className="print-area space-y-10 text-sm">
            <section>
              <h2 className="font-heading font-bold text-lg mb-4">{t("contracts.contractDoc")}</h2>
              <p><strong>{t("contracts.parties")}:</strong> {entityName} — {contract.counterparty}</p>
              <p className="mt-1"><strong>{t("contracts.start")}:</strong> {contract.start_date || "—"} · <strong>{t("contracts.end")}:</strong> {contract.end_date || "—"}</p>
              <h3 className="font-semibold mt-4 mb-2">{t("contracts.object")}</h3>
              <table className="w-full">
                <tbody>
                  {(contract.lines || []).map((l, i) => (
                    <tr key={i}>
                      <td className="py-1">{l.description}</td>
                      <td className="py-1 text-right font-mono">{l.quantity} × {fmt(l.unit_price)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold">
                    <td className="py-1">{t("common.total")}</td>
                    <td className="py-1 text-right font-mono">{fmt(contract.value)}</td>
                  </tr>
                </tbody>
              </table>
              {sign}
            </section>
            <section>
              <h2 className="font-heading font-bold text-lg mb-4">{t("aml.formTitle")}</h2>
              <table className="w-full">
                <tbody>
                  {[["aml.subject", data.aml.subject], ["aml.risk", data.aml.risk_level],
                    ["aml.owner", data.aml.beneficial_owner || "________________"], ["aml.checkDate", data.aml.check_date || today],
                    ["vault.docType", "________________"], ["common.notes", data.aml.notes || ""]].map(([k, v]) => (
                    <tr key={k}><td className="py-1.5 w-48 font-medium">{t(k)}</td><td className="py-1.5">{v}</td></tr>
                  ))}
                </tbody>
              </table>
              {sign}
            </section>
            <section>
              <h2 className="font-heading font-bold text-lg mb-4">{t("privacy.formTitle")}</h2>
              <table className="w-full">
                <tbody>
                  {[["privacy.purpose", data.privacy.purpose], ["privacy.legalBasis", data.privacy.legal_basis],
                    ["privacy.categories", data.privacy.data_categories], ["privacy.retention", data.privacy.retention]].map(([k, v]) => (
                    <tr key={k}><td className="py-1.5 w-48 font-medium">{t(k)}</td><td className="py-1.5">{v}</td></tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-muted-foreground">{data.privacy.notes}</p>
              {sign}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Contracts() {
  const { t, fmt, entity } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", counterparty: "", start_date: "", end_date: "", value: "", status: "active", notes: "" });
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState({});
  const [formsFor, setFormsFor] = useState(null);

  const load = useCallback(() => {
    api.get(`/contracts?entity_id=${entity}`).then((r) => setItems(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get(`/contacts?entity_id=${entity}`).then((r) => setClients(r.data.filter((c) => c.type === "client"))).catch(() => {});
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const chosenLines = services.filter((s) => sel[s.id]).map((s) => ({
    description: s.name, quantity: parseFloat(sel[s.id]) || 1,
    unit_price: s.unit_price, vat_rate: s.vat_rate,
  }));
  const selectedTotal = chosenLines.reduce((s2, l) => s2 + l.quantity * l.unit_price * (1 + l.vat_rate / 100), 0);

  const submit = async () => {
    try {
      await api.post("/contracts", {
        ...form, entity_id: entity,
        value: chosenLines.length ? selectedTotal : (parseFloat(form.value) || 0),
        start_date: form.start_date || null, end_date: form.end_date || null,
        lines: chosenLines.length ? chosenLines : null,
      });
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm({ title: "", counterparty: "", start_date: "", end_date: "", value: "", status: "active", notes: "" });
      setSel({});
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const emailContract = (c) => {
    const client = clients.find((x) => x.name === c.counterparty);
    const linesTxt = (c.lines || []).map((l) => `- ${l.description}: ${l.quantity} x ${l.unit_price} (IVA ${l.vat_rate}%)`).join("\n");
    const body = [
      `Gentile ${c.counterparty},`, "",
      `in allegato trova il contratto "${c.title}" e la modulistica antiriciclaggio e privacy da firmare.`, "",
      linesTxt ? `Prestazioni:\n${linesTxt}\n` : "",
      `Valore: ${c.value}`, "",
      "Cordiali saluti,", "TrustMaster Advisor",
    ].join("\n");
    window.location.href = `mailto:${client?.email || ""}?subject=${encodeURIComponent("Contratto: " + c.title)}&body=${encodeURIComponent(body)}`;
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contracts/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const [genFor, setGenFor] = useState(null);
  const [genForm, setGenForm] = useState({ count: 1, amount_each: "", first_date: new Date().toISOString().slice(0, 10), interval_months: 1 });
  const generate = async () => {
    try {
      await api.post(`/contracts/${genFor.id}/generate-invoices`, {
        count: parseInt(genForm.count) || 1,
        amount_each: parseFloat(genForm.amount_each) || 0,
        first_date: genForm.first_date,
        interval_months: parseInt(genForm.interval_months) || 1,
      });
      toast.success(t("msg.saved"));
      setGenFor(null);
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const statusLabel = (s) => s === "active" ? t("contracts.active") : s === "expired" ? t("contracts.expired") : t("contracts.draft");

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("nav.contracts")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-contract-button"><Plus className="h-4 w-4 mr-2" />{t("contracts.new")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("contracts.new")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("contracts.title2")}</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="contract-title-input" /></div>
              <div><Label>{t("contracts.counterparty")}</Label>
                <Input value={form.counterparty} onChange={(e) => set("counterparty", e.target.value)} list="contract-clients" data-testid="contract-counterparty-input" />
                <datalist id="contract-clients">
                  {clients.map((c) => <option key={c.id} value={c.name} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("contracts.start")}</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="contract-start-input" /></div>
                <div><Label>{t("contracts.end")}</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="contract-end-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("contracts.value")}</Label>
                  <Input type="number" step="0.01" value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="contract-value-input" /></div>
                <div><Label>{t("common.status")}</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="contract-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("contracts.active")}</SelectItem>
                      <SelectItem value="draft">{t("contracts.draft")}</SelectItem>
                      <SelectItem value="expired">{t("contracts.expired")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border border-border rounded-lg p-3 space-y-2">
                <div className="text-sm font-semibold">{t("svc.select")}</div>
                {services.length === 0 && <div className="text-xs text-muted-foreground">{t("svc.empty")}</div>}
                {services.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`service-pick-${s.id}`}>
                    <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={!!sel[s.id]}
                      onChange={(e) => setSel((prev) => { const n = { ...prev }; if (e.target.checked) n[s.id] = 1; else delete n[s.id]; return n; })} />
                    <span className="flex-1 truncate">{s.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{fmt(s.unit_price, s.currency)}</span>
                    {sel[s.id] && (
                      <Input type="number" min="1" step="1" className="w-16 h-7 text-xs" value={sel[s.id]}
                        onChange={(e) => setSel((prev) => ({ ...prev, [s.id]: e.target.value }))} data-testid={`service-qty-${s.id}`} />
                    )}
                  </div>
                ))}
                {chosenLines.length > 0 && (
                  <div className="text-right font-mono font-semibold text-sm pt-1 border-t border-border" data-testid="selected-services-total">
                    {t("svc.selectedTotal")}: {fmt(selectedTotal)}
                  </div>
                )}
              </div>
              <div><Label>{t("common.notes")}</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="contract-notes-input" /></div>
              <Button className="w-full" onClick={submit} data-testid="contract-submit-button">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Tabs defaultValue="records">
        <TabsList className="mb-4">
          <TabsTrigger value="records" data-testid="contracts-subtab-records">{t("forms.records")}</TabsTrigger>
          <TabsTrigger value="docs" data-testid="contracts-subtab-docs">{t("forms.docsTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="docs"><DocTemplatesPanel category="contratto" /></TabsContent>
        <TabsContent value="records">
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
            <Table data-testid="contracts-list-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("contracts.title2")}</TableHead>
                  <TableHead>{t("contracts.counterparty")}</TableHead>
                  <TableHead>{t("contracts.start")}</TableHead>
                  <TableHead>{t("contracts.end")}</TableHead>
                  <TableHead className="text-right">{t("contracts.value")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                    <TableCell className="font-medium">{c.title}</TableCell>
                    <TableCell>{c.counterparty || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{c.start_date || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{c.end_date || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(c.value)}</TableCell>
                    <TableCell>
                      <Badge variant={c.status === "active" ? "default" : c.status === "expired" ? "destructive" : "secondary"}>
                        {statusLabel(c.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" title={t("contracts.forms")} data-testid={`contract-forms-${c.id}`} onClick={() => setFormsFor(c)}>
                          <FileBadge className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title={t("contracts.email")} data-testid={`contract-email-${c.id}`} onClick={() => emailContract(c)}>
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title={t("contracts.generate")} data-testid={`generate-invoices-${c.id}`}
                          onClick={() => { setGenFor(c); setGenForm((f) => ({ ...f, amount_each: c.value || "" })); }}>
                          <FilePlus2 className="h-4 w-4 text-primary" />
                        </Button>
                        <Button size="icon" variant="ghost" data-testid={`delete-contract-${c.id}`} onClick={() => remove(c.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
      <Dialog open={!!genFor} onOpenChange={(o) => !o && setGenFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("contracts.generate")} — {genFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("contracts.invCount")}</Label>
                <Input type="number" min="1" max="60" value={genForm.count} onChange={(e) => setGenForm({ ...genForm, count: e.target.value })} data-testid="gen-count-input" /></div>
              <div><Label>{t("contracts.amountEach")}</Label>
                <Input type="number" step="0.01" value={genForm.amount_each} onChange={(e) => setGenForm({ ...genForm, amount_each: e.target.value })} data-testid="gen-amount-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("contracts.firstDate")}</Label>
                <Input type="date" value={genForm.first_date} onChange={(e) => setGenForm({ ...genForm, first_date: e.target.value })} data-testid="gen-firstdate-input" /></div>
              <div><Label>{t("contracts.interval")}</Label>
                <Input type="number" min="1" max="12" value={genForm.interval_months} onChange={(e) => setGenForm({ ...genForm, interval_months: e.target.value })} data-testid="gen-interval-input" /></div>
            </div>
            <Button className="w-full" onClick={generate} data-testid="gen-submit-button">{t("contracts.generate")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      {formsFor && <ModulisticaDialog contract={formsFor} onClose={() => setFormsFor(null)} />}
    </div>
  );
}
